import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { cloneDeep } from 'lodash';

import FetchModule from "../../../Model/Share/Network/FetchModule";
import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import { ReactComponent as EditSvg } from '../../../image/Edit.svg';
import MoreExplanationView from "./MoreExplanationView";
import SettingInput from "./SettingInput";

export default function AttendanceClassSettingMessageBox(props) {
  const {
    isActivate,
    attendanceClass,
    setAttendanceClass,
    onClickEdit,
  } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const LastAttendanceTimerRef = useRef(null);
  const BeforeReserveTimerRef = useRef(null);

  const [lastAttendanceTimeStr, setLastAttendanceTimeStr] = useState('00:00');

  useEffect(() => {
    const h = String(attendanceClass.lastAttendance.applyTime ?? 0).padStart(2, '0');
    const m = String(attendanceClass.lastAttendance.applyMinute ?? 0).padStart(2, '0');
    setLastAttendanceTimeStr(`${h}:${m}`);
  }, [attendanceClass.lastAttendance.applyTime, attendanceClass.lastAttendance.applyMinute]);

  // SMS/LMS 판별 (90바이트 초과 시 LMS) //
  function getMessageType(text) {
    let bytes = 0;
    for (let i = 0; i < text.length; i++) {
      bytes += text.charCodeAt(i) > 127 ? 2 : 1;
    }
    return bytes > 90 ? 'LMS' : 'SMS';
  }

  // 변수 → 칩 렌더링 //
  function renderText(value) {
    const variableMap = {
      '{store_name}': t('messageSend.variable.storeName'),
      '{user_name}': t('messageSend.variable.userName'),
      '{membership_name}': t('messageSend.variable.membershipName'),
      '{exercise_period}': t('messageSend.variable.exercisePeriod'),
      '{locker_name}': t('messageSend.variable.lockerName'),
      '{coach_name}': t('messageSend.variable.coachName'),
      '{class_name}': t('messageSend.variable.className'),
      '{reserve_time}': t('messageSend.variable.reserveTime'),
      '{class_time}': t('messageSend.variable.classTime'),
      '{holding_start_date}': t('messageSend.variable.holdingStartDate'),
      '{holding_end_date}': t('messageSend.variable.holdingEndDate'),
    };
    return value.split('\n').map((line, lineIdx) => (
      <MessageLine key={lineIdx}>
        {line.split(/(\{[^}]+\})/g).map((part, i) => {
          const label = variableMap[part];
          if (label) return <VariableChip key={i}>{label.replace(/^\{|\}$/g, '')}</VariableChip>;
          return part ? <span key={i}>{part}</span> : null;
        })}
      </MessageLine>
    ));
  }

  // 체크박스 클릭시 //
  function onClickCheckBox(division) {
    if (!isActivate) return;

    let newData = cloneDeep(attendanceClass);
    
    // 출석시 //
    if (division === 'Attendance') {
      newData.attendance.isUseSetting = !newData.attendance.isUseSetting;

      saveMessageSetting(newData.attendance);
    }
    // 마지막 출석 //
    else if (division === 'LastAttendance') {
      newData.lastAttendance.isUseSetting= !newData.lastAttendance.isUseSetting;

      saveMessageSetting(newData.lastAttendance);
    }
    // 예약시 //
    else if (division === 'OnReserve') {
      newData.onReserve.isUseSetting = !newData.onReserve.isUseSetting;

      saveMessageSetting(newData.onReserve);
    }
    // 예약 취소시 //
    else if (division === 'CancelReserve') {
      newData.cancelReserve.isUseSetting = !newData.cancelReserve.isUseSetting;

      saveMessageSetting(newData.cancelReserve);
    }
    // 예약 ?시간 전 //
    else if (division === 'BeforeReserve') {
      newData.beforeReserve.isUseSetting = !newData.beforeReserve.isUseSetting;

      saveMessageSetting(newData.beforeReserve);
    }
    // 결석 시 //
    else if (division === 'Absence') {
      newData.absence.isUseSetting = !newData.absence.isUseSetting;

      saveMessageSetting(newData.absence);
    }
  }

  // 마지막 출석 일수 변경시 //
  function onChangeLastAttendanceDay(value) {
    if (!isActivate) return;

    if (value < 100) {
      let newData = cloneDeep(attendanceClass);

      newData.lastAttendance.applyDay = value;

      setAttendanceClass(newData);

      if (LastAttendanceTimerRef.current !== null) clearTimeout(LastAttendanceTimerRef.current);

      LastAttendanceTimerRef.current = setTimeout(async () => {
        saveMessageSetting(newData.lastAttendance);
      }, 3000);
    }
  }

  // 마지막 출석 시:분 변경시 (HH:MM 문자열) //
  function onChangeLastAttendanceTimeStr(value) {
    if (!isActivate) return;

    setLastAttendanceTimeStr(value);

    if (value.length === 5) {
      const [hStr, mStr] = value.split(':');
      const hour = parseInt(hStr, 10);
      const minute = parseInt(mStr, 10);

      if (!isNaN(hour) && hour >= 0 && hour < 24 && !isNaN(minute) && minute >= 0 && minute <= 59) {
        let newData = cloneDeep(attendanceClass);
        newData.lastAttendance.applyTime = hour;
        newData.lastAttendance.applyMinute = minute;

        setAttendanceClass(newData);

        if (LastAttendanceTimerRef.current !== null) clearTimeout(LastAttendanceTimerRef.current);
        LastAttendanceTimerRef.current = setTimeout(() => saveMessageSetting(newData.lastAttendance), 3000);
      }
    }
  }

  // 예약된 수업 시작시간 변경시 //
  function onChangeBeforeReserveTime(value) {
    if (!isActivate) return;

    if (value !== '') {
      let newData = cloneDeep(attendanceClass);

      newData.beforeReserve.applyTime = value;

      setAttendanceClass(newData);

      if (BeforeReserveTimerRef.current !== null) clearTimeout(BeforeReserveTimerRef.current);

      BeforeReserveTimerRef.current = setTimeout(async () => {
        saveMessageSetting(newData.beforeReserve);
      }, 3000);
    }
  }

  // 일정 시간 경과 후 데이터 저장 //
  function saveMessageSetting(data) {
    let requestData = {
      id: data.id,
      storeId: data.storeId,
      division: data.division,
      text: data.text,
      title: data.title,
      applyDay: data.applyDay,
      applyTime: data.applyTime,
      applyMinute: data.applyMinute ?? 0,
      isUseSetting: data.isUseSetting
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('message_setting/save', 'POST', requestData);

        if (responseData.status === 200) {
            let newData = cloneDeep(attendanceClass);

            // 출석시 //
            if (data.division === 'Attendance') newData.attendance = responseData.data;
            // 마지막 출석 //
            else if (data.division === 'LastAttendance') newData.lastAttendance = responseData.data;
            // 예약시 //
            else if (data.division === 'OnReserve') newData.onReserve = responseData.data;
            // 예약 취소시 //
            else if (data.division === 'CancelReserve') newData.cancelReserve = responseData.data;
            // 예약 ?시간 전 //
            else if (data.division === 'BeforeReserve') newData.beforeReserve = responseData.data;
            // 결석 시 //
            else if (data.division === 'Absence') newData.absence = responseData.data;

            setAttendanceClass(newData);
        }
      }
    )();
  }

  return (
    <TopLevelWrapper>
      <DivisionBox>
        <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.primary}>{t('messageSend.autoMessage.attendanceClass')}</Text>
        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{t('messageSend.editModal.descAttendanceClassSetting')}</Text>
      </DivisionBox>
      <SettingBox>
        <SettingColumnBox>
          <SlideComponentWrapper onClick={() => onClickCheckBox('Attendance')}>
            <SlideOutBox isClicked={attendanceClass.attendance.isUseSetting && isActivate}>
              <SlideInBox isClicked={attendanceClass.attendance.isUseSetting && isActivate} />
            </SlideOutBox>
            <SettingOptionTextWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text} hover='pointer'>{t('messageSend.autoMessage.attendance')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText} hover='pointer'>{t('messageSend.editModal.descImmediatelyOnEntry')}</Text>
            </SettingOptionTextWrapper>
          </SlideComponentWrapper>
          <SettingTextWrapper>
            <TextWrapper>
              <MessageText>{renderText(attendanceClass.attendance.text)}</MessageText>
              <CharCountText>{`${getMessageType(attendanceClass.attendance.text)} ${attendanceClass.attendance.text.length}${t('messageSend.editModal.smsCharCountSuffix')}`}</CharCountText>
            </TextWrapper>
          </SettingTextWrapper>
          <ModifyButton onClick={() => onClickEdit(attendanceClass.attendance)}>
            <EditIcon />
          </ModifyButton>
        </SettingColumnBox>

        <DivisionLine />

        <SettingColumnBox>
          <SlideComponentWrapper>
            <SlideOutBox isClicked={attendanceClass.lastAttendance.isUseSetting && isActivate} onClick={() => onClickCheckBox('LastAttendance')}>
              <SlideInBox isClicked={attendanceClass.lastAttendance.isUseSetting && isActivate} />
            </SlideOutBox>
            <CheckBoxTextWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text}>{t('messageSend.autoMessage.lastAttendance')}</Text>
              <SettingCaptionTextWrapper>
                <InputWrapper onClick={e => e.stopPropagation()}>
                  <SettingInput
                    value={attendanceClass.lastAttendance.applyDay}
                    setValue={onChangeLastAttendanceDay}
                    placeholder={0}/>
                </InputWrapper>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{t('messageSend.autoMessage.daysAfter')}</Text>
                <InputWrapper style={{ width: '73px' }} onClick={e => e.stopPropagation()}>
                  <SettingInput
                    value={lastAttendanceTimeStr}
                    setValue={onChangeLastAttendanceTimeStr}
                    placeholder='00:00'
                    isTimeType={true} />
                </InputWrapper>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{t('messageSend.autoMessage.atTime')}</Text>
              </SettingCaptionTextWrapper>
            </CheckBoxTextWrapper>
          </SlideComponentWrapper>
          <SettingTextWrapper>
            <TextWrapper>
              <MessageText>{renderText(attendanceClass.lastAttendance.text)}</MessageText>
              <CharCountText>{`${getMessageType(attendanceClass.lastAttendance.text)} ${attendanceClass.lastAttendance.text.length}${t('messageSend.editModal.smsCharCountSuffix')}`}</CharCountText>
            </TextWrapper>
          </SettingTextWrapper>
          <ModifyButton onClick={() => onClickEdit(attendanceClass.lastAttendance)}>
            <EditIcon />
          </ModifyButton>
        </SettingColumnBox>

        <DivisionLine />

        <SettingColumnBox>
          <SlideComponentWrapper onClick={() => onClickCheckBox('OnReserve')}>
            <SlideOutBox isClicked={attendanceClass.onReserve.isUseSetting && isActivate}>
              <SlideInBox isClicked={attendanceClass.onReserve.isUseSetting && isActivate} />
            </SlideOutBox>
            <SettingOptionTextWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text} hover='pointer'>{t('messageSend.editModal.descOnReserve')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText} hover='pointer'>{t('messageSend.editModal.descImmediatelyAfterBooking')}</Text>
            </SettingOptionTextWrapper>
            <MoreExplanationViewWrapper>
              <MoreExplanationView text={t('messageSend.autoMessage.reserveTooltip')} />
            </MoreExplanationViewWrapper>
          </SlideComponentWrapper>
          <SettingTextWrapper>
            <TextWrapper>
              <MessageText>{renderText(attendanceClass.onReserve.text)}</MessageText>
              <CharCountText>{`${getMessageType(attendanceClass.onReserve.text)} ${attendanceClass.onReserve.text.length}${t('messageSend.editModal.smsCharCountSuffix')}`}</CharCountText>
            </TextWrapper>
          </SettingTextWrapper>
          <ModifyButton onClick={() => onClickEdit(attendanceClass.onReserve)}>
            <EditIcon />
          </ModifyButton>
        </SettingColumnBox>

        <DivisionLine />

        <SettingColumnBox>
          <SlideComponentWrapper onClick={() => onClickCheckBox('CancelReserve')}>
            <SlideOutBox isClicked={attendanceClass.cancelReserve.isUseSetting && isActivate}>
              <SlideInBox isClicked={attendanceClass.cancelReserve.isUseSetting && isActivate} />
            </SlideOutBox>
            <SettingOptionTextWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text} hover='pointer'>{t('messageSend.editModal.descCancelReserve')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText} hover='pointer'>{t('messageSend.editModal.descImmediatelyOnCancellation')}</Text>
            </SettingOptionTextWrapper>
            <MoreExplanationViewWrapper>
              <MoreExplanationView text={t('messageSend.autoMessage.cancelReserveTooltip')} />
            </MoreExplanationViewWrapper>
          </SlideComponentWrapper>
          <SettingTextWrapper>
            <TextWrapper>
              <MessageText>{renderText(attendanceClass.cancelReserve.text)}</MessageText>
              <CharCountText>{`${getMessageType(attendanceClass.cancelReserve.text)} ${attendanceClass.cancelReserve.text.length}${t('messageSend.editModal.smsCharCountSuffix')}`}</CharCountText>
            </TextWrapper>
          </SettingTextWrapper>
          <ModifyButton onClick={() => onClickEdit(attendanceClass.cancelReserve)}>
            <EditIcon />
          </ModifyButton>
        </SettingColumnBox>

        <DivisionLine />

        <SettingColumnBox>
          <SlideComponentWrapper>
            <SlideOutBox isClicked={attendanceClass.beforeReserve.isUseSetting && isActivate} onClick={() => onClickCheckBox('BeforeReserve')}>
              <SlideInBox isClicked={attendanceClass.beforeReserve.isUseSetting && isActivate} />
            </SlideOutBox>
            <CheckBoxTextWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text}>{t('messageSend.autoMessage.reservedClass')}</Text>
              <SettingCaptionTextWrapper>
                <InputWrapper onClick={e => e.stopPropagation()}>
                  <SettingInput
                    value={attendanceClass.beforeReserve.applyTime}
                    setValue={onChangeBeforeReserveTime}
                    placeholder={0}/>
                </InputWrapper>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{t('messageSend.autoMessage.hoursBefore')}</Text>
              </SettingCaptionTextWrapper>
            </CheckBoxTextWrapper>
          </SlideComponentWrapper>
          <SettingTextWrapper>
            <TextWrapper>
              <MessageText>{renderText(attendanceClass.beforeReserve.text)}</MessageText>
              <CharCountText>{`${getMessageType(attendanceClass.beforeReserve.text)} ${attendanceClass.beforeReserve.text.length}${t('messageSend.editModal.smsCharCountSuffix')}`}</CharCountText>
            </TextWrapper>
          </SettingTextWrapper>
          <ModifyButton onClick={() => onClickEdit(attendanceClass.beforeReserve)}>
            <EditIcon />
          </ModifyButton>
        </SettingColumnBox>

        <DivisionLine />

        <SettingColumnBox>
          <SlideComponentWrapper onClick={() => onClickCheckBox('Absence')}>
            <SlideOutBox isClicked={attendanceClass.absence.isUseSetting && isActivate}>
              <SlideInBox isClicked={attendanceClass.absence.isUseSetting && isActivate} />
            </SlideOutBox>
            <SettingOptionTextWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text} hover='pointer'>{t('messageSend.editModal.descAbsence')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText} hover='pointer'>{t('messageSend.editModal.descRightAfterClassEnds')}</Text>
            </SettingOptionTextWrapper>
          </SlideComponentWrapper>
          <SettingTextWrapper>
            <TextWrapper>
              <MessageText>{renderText(attendanceClass.absence.text)}</MessageText>
              <CharCountText>{`${getMessageType(attendanceClass.absence.text)} ${attendanceClass.absence.text.length}${t('messageSend.editModal.smsCharCountSuffix')}`}</CharCountText>
            </TextWrapper>
          </SettingTextWrapper>
          <ModifyButton onClick={() => onClickEdit(attendanceClass.absence)}>
            <EditIcon />
          </ModifyButton>
        </SettingColumnBox>
      </SettingBox>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const DivisionBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  height: 53px;

  gap: 6px;
`;

const SettingBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 10px 20px;

  box-sizing: border-box;
  width: 100%;

  gap: 10px;

  border-radius: 10px;

  background-color: ${props => props.theme.subBackground};
`;

const SettingColumnBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;
  min-height: 40px;
`;

const SettingOptionTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: 100%;

  gap: 3px;
`;

const SlideComponentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding-right: 20px;

  box-sizing: border-box;
  width: 370px;
  height: 100%;

  gap: 30px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SlideOutBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex-shrink: 0;
  box-sizing: border-box;
  width: 70px;
  height: 32px;

  border: ${props => props.isClicked ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.line}`};
  border-radius: 9px;
  background-color: ${props => props.isClicked ? props.theme.primary : props.theme.subBackground};

  transition: all 0.3s ease-in-out;
`;

const SlideInBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-left: ${props => props.isClicked ? '43px' : '4px'};

  width: 22px;
  height: 22px;

  border-radius: 7px;
  background-color: ${props => props.isClicked ? props.theme.white : props.theme.line};

  transition: all 0.3s ease-in-out;
`;

const CheckBoxTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  gap: 5px;
`;

const SettingCaptionTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 50px;
  height: 30px;
`;

const MoreExplanationViewWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-left: auto;
`;

const SettingTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 566px;
  height: 100%;

  border-left: 1px solid ${props => props.theme.line};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  padding: 10px 15px;

  gap: 5px;

  box-sizing: border-box;
  width: 100%;
`;

const MessageText = styled.div`
  display: block;
`;

const MessageLine = styled.div`
  display: block;

  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 21px;
  color: ${props => props.theme.text};

  &:not(:last-child) {
    margin-bottom: 5px;
  }
`;

const CharCountText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.subText};

  margin: 0;
`;

const VariableChip = styled.span`
  display: inline-flex;
  align-items: center;

  padding: 0 5px;

  height: 21px;

  border-radius: 5px;
  background-color: ${props => props.theme.primaryAlpha20};

  font-family: NotoSansKR-Regular;
  font-size: 14px;
  color: ${props => props.theme.text};

  transition: all 0.3s ease-in-out;
`;

const EditIcon = styled(EditSvg)`
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.line};
  }

  transition: all 0.3s ease-in-out;
`;

const ModifyButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-end;

  width: 59px;
  height: 100%;

  background-color: ${props => props.theme.subBackground};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const DivisionLine = styled.div`
  width: calc(100% - 20px);
  height: 1px;

  margin: 10px 0px;

  background-color: ${props => props.theme.line};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.hover};
    }
  }

  transition: all 0.3s ease-in-out;
`;
